import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { action, computed } from 'mobx'

/**
 * The payout awaiting confirmation.
 */
export interface ConfirmPayoutSummary {
  total: number
  transactionCount: number
}

/**
 * View model for confirming the payout submission.
 */
export class ConfirmPayoutDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  constructor(private readonly _getSummary: () => ConfirmPayoutSummary) {}

  /**
   * The approved total, kept current while the dialog is open.
   */
  @computed
  get total() {
    return this._getSummary().total
  }

  /**
   * The number of approved transactions, kept current while the dialog is
   * open.
   */
  @computed
  get transactionCount() {
    return this._getSummary().transactionCount
  }

  /**
   * Asks the user to confirm the payout.
   *
   * @returns whether the user confirmed.
   */
  async prompt(signal?: AbortSignal) {
    this.modal.close()
    const responsePromise = this._prompt.prompt(signal)
    this.modal.show()
    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Confirms the payout.
   */
  @action.bound
  confirm() {
    this._prompt.respond(true)
  }
}
